import { Avatar, Box, Menu, ScrollArea, Text, TextInput, UnstyledButton } from "@mantine/core"
import { CaretDownIcon, MagnifyingGlassIcon, StorefrontIcon } from "@phosphor-icons/react"
import { matchesBusinessName } from "@dehub/theme"
import React from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { NetworkApi } from "../../apis"
import { renderBusinessName } from "../../utils/render-business-name"
import classes from "./product-list/catalog.module.css"

export const ALL_SUPPLIERS = "ALL"

type Props = {
  suppliers: any[]
  /** A supplier id, or ALL_SUPPLIERS. */
  value: string
  onChange: (value: string) => void
  /** Offer «Бүх нийлүүлэгч» (only meaningful with more than one supplier). */
  allowAll?: boolean
}

const initials = (name: string) => (name.trim()[0] || "?").toUpperCase()

function SupplierAvatar({ supplier }: Readonly<{ supplier: any | null }>) {
  if (!supplier) {
    return (
      <Avatar size={34} radius="sm" color="dark" variant="light">
        <StorefrontIcon size={18} />
      </Avatar>
    )
  }
  const name = renderBusinessName(supplier, "")
  return (
    <Avatar size={34} radius="sm" color="brand" variant="filled" src={supplier.logo || null} alt="">
      {initials(name)}
    </Avatar>
  )
}

/**
 * Header supplier picker (canvas OrdB_Catalog «Захиалга [НЭКСТ ДИСТРИБЬЮШН ХХК ▾]», finding B20).
 * Replaces the old full-page supplier list that nothing imported any more (B33).
 */
export function SupplierSelect({ suppliers, value, onChange, allowAll = false }: Props) {
  const { t } = useTranslation("order")
  const [query, setQuery] = React.useState("")
  const current = suppliers.find((s) => s.id === value) || null
  const showAll = allowAll && suppliers.length > 1

  const filtered = React.useMemo(() => {
    const q = query.trim()
    if (!q) return suppliers
    return suppliers.filter((s) => matchesBusinessName(s, q) || String(s.regNumber || "").includes(q))
  }, [suppliers, query])

  // Canvas OrdB_Catalog shows the network's main payment term under the supplier name; the
  // select list only carries the business, so read it from the network (cached per supplier).
  const { data: network } = useSWR<any>(
    current ? `swr.buyer.network.${current.id}` : null,
    () => NetworkApi.get(current.id).catch(() => null),
    { revalidateOnFocus: false },
  )
  const mainTerm: string | undefined = network?.paymentTerm?.description

  const name = current ? renderBusinessName(current, "-") : t("catalog.supplierAll")
  const sub = current
    ? mainTerm
      ? t("catalog.mainTerm", { value: mainTerm })
      : current.regNumber
        ? t("catalog.regNumber", { value: current.regNumber })
        : null
    : t("catalog.supplierCount", { count: suppliers.length })

  return (
    <Menu position="bottom-start" shadow="md" radius="md" width={340} onClose={() => setQuery("")}>
      <Menu.Target>
        <UnstyledButton className={classes.supplierTrigger} aria-label={`${t("catalog.supplierLabel")}: ${name}`}>
          <SupplierAvatar supplier={current} />
          <Box miw={0} style={{ flex: 1 }}>
            <Text className={classes.supplierName}>{name}</Text>
            {sub && <Text className={classes.supplierSub}>{sub}</Text>}
          </Box>
          <CaretDownIcon size={14} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {suppliers.length > 8 && (
          <TextInput
            m={4}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder={t("catalog.supplierSearch")}
            aria-label={t("catalog.supplierSearch")}
            leftSection={<MagnifyingGlassIcon size={16} />}
            data-autofocus
          />
        )}
        <ScrollArea.Autosize mah={360} type="auto">
          {showAll && !query && (
            <Menu.Item
              className={classes.supplierOption}
              data-selected={value === ALL_SUPPLIERS || undefined}
              leftSection={<SupplierAvatar supplier={null} />}
              onClick={() => onChange(ALL_SUPPLIERS)}
            >
              <Text fz="var(--nw-fz-body)" fw={600}>
                {t("catalog.supplierAll")}
              </Text>
              <Text className={classes.meta}>{t("catalog.supplierCount", { count: suppliers.length })}</Text>
            </Menu.Item>
          )}
          {filtered.map((s) => (
            <Menu.Item
              key={s.id}
              className={classes.supplierOption}
              data-selected={value === s.id || undefined}
              leftSection={<SupplierAvatar supplier={s} />}
              onClick={() => onChange(s.id)}
            >
              <Text fz="var(--nw-fz-body)" fw={600} lineClamp={1}>
                {renderBusinessName(s, "-")}
              </Text>
              {s.regNumber && <Text className={classes.meta}>{t("catalog.regNumber", { value: s.regNumber })}</Text>}
            </Menu.Item>
          ))}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  )
}
